import { loadAltcha } from './loadAltcha';
import { useEffect, useRef, useState } from 'react';
import type { CaptchaCharacteristic } from '../../schema';
import { useTranslation } from '../../i18n';
import type { Locale } from '../../i18n/types';
import {
    useCharacteristicSelection,
    useCharacteristicValidationErrors,
    useStoreActions,
} from '../../state/StoreContext';
import { useWebshopContext } from '../../state/WebshopContext';

/**
 * Maps configurator locales onto the locale codes altcha v3 ships
 * translations for under `altcha/i18n/{code}.js`. Each loaded locale
 * registers itself on `globalThis.$altcha.i18n` and the widget then
 * picks the active one via its `language` attribute.
 */
const ALTCHA_LOCALES: Record<Locale, string> = {
    de_DE: 'de',
    en_US: 'en',
    fr_FR: 'fr-fr',
    es_ES: 'es-es',
    it_IT: 'it',
    nl_NL: 'nl',
};

/**
 * Altcha proof-of-work captcha.
 *
 * The `<altcha-widget>` Web Component is the actual UI — it auto-fetches a
 * challenge from the configured URL, computes the PoW, and emits a
 * `statechange` event when verified. We lazy-load the `altcha` package on
 * first render so the ~20 KB widget code lives in its own chunk (only
 * customers using a captcha pay the cost).
 *
 * The base64 payload from the widget is stored as `{ kind: 'captcha',
 * payload }` in the store. Save-Button-Actions submit it back; the server
 * re-verifies via {@link \App\Services\Altcha\AltchaService}.
 */
export function CaptchaView({ characteristic }: { characteristic: CaptchaCharacteristic }) {
    const { t, locale } = useTranslation();
    const webshop = useWebshopContext();
    const selection = useCharacteristicSelection(characteristic.id);
    const validationErrors = useCharacteristicValidationErrors(characteristic.id);
    const { setCaptchaPayload } = useStoreActions();
    const widgetRef = useRef<HTMLElement | null>(null);
    const [loaded, setLoaded] = useState(false);
    // Bumped to remount the widget, which then solves a fresh challenge.
    const [round, setRound] = useState(0);
    const hadPayload = useRef(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const currentPayload = selection?.kind === 'captcha' ? selection.payload : '';
    const challengeUrl = challengeEndpoint(webshop?.cloudBaseUrl, characteristic.captchaComplexity);

    // Detect environments where altcha can't be safely mounted. The v3
    // altcha widget calls `new URL(location.origin)` inside its Svelte
    // component initialiser — and in `srcdoc` iframes (the admin preview)
    // some browsers return the literal string "null" for location.origin,
    // which makes the URL constructor throw before the widget can even
    // render. We pre-empt that by rendering a placeholder and seeding the
    // store with a sentinel value so client-side form validation doesn't
    // block — the server-side AltchaService still rejects the sentinel,
    // so the security gate stays intact on real submissions.
    const isUnsafeOrigin = typeof window !== 'undefined'
        && (window.location.origin === 'null' || window.location.protocol === 'about:');

    useEffect(() => {
        if (isUnsafeOrigin && currentPayload === '') {
            setCaptchaPayload(characteristic.id, PREVIEW_PLACEHOLDER_PAYLOAD);
        }
    }, [isUnsafeOrigin, currentPayload, characteristic.id, setCaptchaPayload]);

    // Lazy-load the widget (with all its translations). Loading registers the
    // `<altcha-widget>` custom element globally; the `language` attribute
    // picks the strings.
    useEffect(() => {
        if (isUnsafeOrigin) return;
        let cancelled = false;
        (async () => {
            try {
                await loadAltcha();
                if (!cancelled) setLoaded(true);
            } catch (err: unknown) {
                if (!cancelled) {
                    const msg = err instanceof Error ? err.message : String(err);
                    setLoadError(msg);
                    console.warn('[Configento] altcha widget failed to load', err);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [isUnsafeOrigin]);

    // A solution is good for one submission. When the payload is cleared
    // after being set (the action button used it, or it expired), start a
    // new round so the widget solves again on its own.
    useEffect(() => {
        if (isUnsafeOrigin) return;
        if (currentPayload !== '') {
            hadPayload.current = true;
        } else if (hadPayload.current) {
            hadPayload.current = false;
            setRound((r) => r + 1);
        }
    }, [currentPayload, isUnsafeOrigin]);

    // Bind / unbind verification listeners. v3 widget emits `verified` with
    // `{ payload }` on success and `expired` on TTL expiry. We listen on
    // the widget element itself rather than the document so multiple
    // captcha instances on the same page don't cross-contaminate.
    useEffect(() => {
        const el = widgetRef.current;
        if (!loaded || !el) return;

        const onVerified = (event: Event) => {
            const detail = (event as CustomEvent<{ payload?: string }>).detail ?? {};
            if (typeof detail.payload === 'string' && detail.payload !== '') {
                setCaptchaPayload(characteristic.id, detail.payload);
            }
        };
        const onCleared = () => setCaptchaPayload(characteristic.id, '');

        el.addEventListener('verified', onVerified);
        el.addEventListener('expired', onCleared);
        return () => {
            el.removeEventListener('verified', onVerified);
            el.removeEventListener('expired', onCleared);
        };
    }, [loaded, round, characteristic.id, setCaptchaPayload]);

    if (isUnsafeOrigin) {
        return (
            <div className="cfg-captcha-preview text-xs text-gray-500 italic p-2 border border-dashed border-gray-300 rounded">
                {t('captcha.preview_placeholder')}
            </div>
        );
    }

    if (loadError !== null) {
        return (
            <div className="text-xs text-red-600">
                {t('captcha.load_failed')}
            </div>
        );
    }

    if (!loaded) {
        return (
            <div className="text-xs text-gray-500 animate-pulse" aria-live="polite">
                {t('captcha.loading')}
            </div>
        );
    }

    // `altcha-widget` is a Web Component. TypeScript doesn't know its
    // attribute shape — cast through `any` once at the boundary instead of
    // declaring global JSX types (would leak into consumers).
    //
    // Attributes recognised by v3.x: auto, challenge, configuration,
    // display, language, name, theme, type, workers (see altcha.js
    // observedAttributes around line 7344). Earlier altcha versions used
    // `challengeurl` — DO NOT pass that, v3 ignores it and `challenge`
    // ends up null → "null cannot be parsed as a URL" on connectedCallback.
    const widgetProps = {
        ref: widgetRef as React.RefObject<HTMLElement>,
        challenge: challengeUrl,
        auto: 'onload',
        language: ALTCHA_LOCALES[locale] ?? 'en',
    } as Record<string, unknown>;
    const WidgetTag = 'altcha-widget' as unknown as React.JSXElementConstructor<Record<string, unknown>>;

    return (
        <div
            className={(characteristic.cssClass ?? '').trim() || undefined}
            data-captcha-payload={currentPayload === '' ? 'empty' : 'set'}
            data-configento-char-id={characteristic.id}
            data-configento-char-type="captcha"
            data-configento-invalid={validationErrors.length > 0 || undefined}
        >
            <WidgetTag key={round} {...widgetProps} />
            {validationErrors.length > 0 && (
                <ul className="cfg-char__errors" role="alert">
                    {validationErrors.map((err, idx) => (
                        <li key={`${err.code}-${idx}`} className="cfg-char__error">
                            {err.message}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function challengeEndpoint(cloudBaseUrl: string | undefined, complexity: 'easy' | 'medium' | 'hard'): string {
    // The preview / storefront context ships `cloudBaseUrl` as a hostname
    // (`cloud.configento.app`), NOT a full URL — same convention as
    // webservice.ts:buildProxyUrl. We need to prepend `https://` ourselves;
    // otherwise altcha resolves the URL relative to `location.origin` and
    // ends up requesting `admin.configento.app/preview/v3/<hostname>/api/altcha/challenge`
    // which 404s.
    const raw = (cloudBaseUrl ?? 'cloud.configento.app').replace(/\/+$/, '');
    const protocol = raw.startsWith('http') ? '' : 'https://';
    return `${protocol}${raw}/api/altcha/challenge?complexity=${encodeURIComponent(complexity)}`;
}

/**
 * Sentinel payload used in preview/sandboxed contexts where the real
 * altcha widget can't mount. AltchaService::verifyPayload rejects this on
 * the server (the proof signature won't validate), so the security gate
 * stays intact for any real submission attempt from a preview.
 */
const PREVIEW_PLACEHOLDER_PAYLOAD = 'preview-placeholder';

